<template>
  <div class="segment">
    <template
      v-for="section in sections"
      :key="section.name"
    >
      <ul :class="{ 'selected-block': selectedBlockLabel === section.name }">
        <li
          :class="{ hasChild: hasLinksMoreThenTwo(section) }"
          @mouseover="mouseOverLeft(section)"
          @mouseout="mouseOutLeft(section)"
        >
          <label v-if="!hasLinksMoreThenTwo(section)">
            <NuxtLink
              v-if="section.links"
              v-track-event="{ eventName: 'Header', component: { zone: 'header', element: 'dn-burger-menu', element_id: 'menu-expand' }, attributes: { element_text: section.links[0].name } }"
              :to="section.links[0].url"
              :external="!!section.links[0].external"
            >
              <span>{{ section.links[0].name }}</span>
            </NuxtLink>
          </label>
          <label
            v-else
            v-track-event="{ eventName: 'Header', component: { zone: 'header', element: 'dn-burger-menu', element_id: 'menu-expand' }, attributes: { element_text: section.name } }"
            @click.prevent="$emit('update-selected-block-label', section.name)"
          >
            <span>{{ section.name }}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 16 27"
              width="13"
              height="25"
              fill="none"
            >
              <path
                d="M1 1L14 13.5L1 26"
                stroke-width="2"
              />
            </svg>
          </label>
          <ul>
            <template
              v-for="(link, index) in section.links"
              :key="link.name + index"
            >
              <li
                @click="$emit('hide-main-menu')"
              >
                <NuxtLink
                  v-track-event="{ eventName: 'Header', component: { zone: 'header', element: 'dn-burger-menu', element_id: 'menu-navigation' }, attributes: { link_url: link.url } }"
                  :to="link.url"
                  :external="!!link.external"
                >
                  <span>{{ link.name }}</span>
                </NuxtLink>
              </li>
            </template>
          </ul>
        </li>
      </ul>
    </template>
    <dn-link
      v-if="type === 'bottom'"
      v-track-event="{ eventName: 'Tips oss', component: { zone: 'header', element: 'dn-tips-oss' } }"
      class="outline"
      text="Tips oss"
      href="/staticprojects/2020/11/tips-oss"
      external
    />
  </div>
</template>

<script setup lang="ts">
import { type PropType } from 'vue';
import { type Sections } from '~/types';

const emit = defineEmits(['update-selected-block-label', 'mouse-over-left', 'mouse-out-left', 'hide-main-menu']);
const props = defineProps({
  type: {
    type: String,
    default: function () {
      return '';
    }
  },
  sections: {
    type: Array as PropType<Sections[]>,
    default: function () {
      return [];
    }
  },
  selectedBlockLabel: {
    type: String,
    default: ''
  }
});
function hasLinksMoreThenTwo (section) {
  return section && Array.isArray(section.links) && section.links.length > 1;
}

function mouseOverLeft (block) {
  if (props.type === 'left' && hasLinksMoreThenTwo(block)) {
    emit('mouse-over-left');
  }
}
function mouseOutLeft (block) {
  if (props.type === 'left' && hasLinksMoreThenTwo(block)) {
    emit('mouse-out-left');
  }
}
</script>

<style lang="scss" scoped>
ul {
  padding: 0;
  margin: 0;

  .segment-left & {
    @media only screen and (width < 1024px) {
      &.selected-block {
        label {
          font-family: var(--typography-editorial-subhead-max-font-family);

          svg {
            transform: rotate(-90deg);

            path {
              stroke-width: 3;
            }
          }
        }

        > li {
          &.hasChild {
            ul {
              display: block;
              opacity: 1;
            }
          }
        }
      }
    }

    @media only screen and (width >= 1024px) {
      width: 300px;

      &:hover {
        label {
          font-family: var(--typography-editorial-subhead-max-font-family);

          svg {
            path {
              stroke-width: 3;
            }
          }
        }

        > li {
          &.hasChild {
            ul {
              display: block;
              opacity: 1;
              z-index: 1;
            }
          }
        }
      }
    }
  }

  > li {
    margin-left: 0;

    a {
      color: var(--color-hamburgermenu-fg);
      text-decoration: none;
    }

    li {
      a {
        display: block;
        padding: 12px 0;
        -webkit-tap-highlight-color: transparent;

        &:hover {
          .segment-left & {
            font-family: var(--typography-editorial-subhead-max-font-family);
          }
        }
      }
    }
  }
}

.segment {
  label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    padding: 16px 0;
    -webkit-tap-highlight-color: transparent;
  }

  &-mid {
    padding: 12px 0 0;

    @media only screen and (width >= 1024px) {
      display: flex;
      gap: var(--row-gap);
      justify-content: space-between;
      padding: 50px 0;
    }

    ul {
      ul {
        padding: 0;

        @media only screen and (width < 1024px) {
          display: none;
          padding: 10px 20px;
        }
      }

      @media only screen and (width < 1024px) {
        &.selected-block {
          li {
            label {
              font-family: var(--typography-editorial-subhead-max-font-family);

              svg {
                transform: rotate(-90deg);

                path {
                  stroke-width: 3;
                }
              }
            }

            ul {
              display: block;
              opacity: 1;
            }
          }
        }
      }
    }
  }

  &-right {
    @media only screen and (width >= 1024px) {
      svg {
        display: none;
      }
    }

    label {
      font-size: 18px;
      line-height: 20px;

      @media only screen and (width >= 1024px) {
        font-family: var(--typography-editorial-subhead-max-font-family);
        font-size: 20px;
        padding: 0;
        margin-bottom: 20px;
      }
    }
  }

  &-left {
    position: relative;
    height: 100%;
    font-size: 24px;
    line-height: 32px;
    padding: 12px 0;

    @media only screen and (width >= 1024px) {
      font-size: 32px;
      line-height: 48px;
      border-right: 1px solid var(--color-hamburgermenu-divider);
      padding: 24px 0;
    }

    + .segment-right {
      .segment-mid {
        @media only screen and (width >= 1024px) {
          padding: 50px 0 50px 50px;
        }
      }
    }

    li {
      padding: 0;

      @media only screen and (width >= 1024px) {
        padding: 0 40px 0 0;
      }

      ul {
        display: none;
        background-color: var(--color-hamburgermenu-bg);
        opacity: 0;
        padding: 10px 20px;
        transition: all 0.2s linear;

        @media only screen and (width >= 1024px) {
          position: absolute;
          right: -401px;
          top: 0;
          width: 400px;
          height: 100%;
          background-color: var(--color-hamburgermenu-bg);
          border-right: 1px solid var(--color-neutral-400);
          padding: 38px 50px;
        }

        @media only screen and (width >= 1536px) {
          padding: 38px 50px 380px;
        }

        li {
          font-size: 16px;
          line-height: 24px;
        }
      }
    }
  }

  &-bottom {
    align-items: center;
    justify-content: flex-end;
    padding: 24px 0;

    @media only screen and (width >= 1024px) {
      display: flex;
      font-size: 12px;
      line-height: 14px;
      padding: 0;
    }

    .dn-link {
      justify-content: center;

      @media only screen and (width < 1024px) {
        padding: 16px;
      }
    }
  }
}
</style>
